import { Box, Center, Stack, Text, useBreakpointValue, Image, useColorModeValue } from "@chakra-ui/react";
import {
  useCollectionCreatedDate,
  useCollectionKey,
  useConfigMap,
  useGetViewConfig,
  useItemSelector,
  useLatestCollectionWorkflowId,
  useDashboardMetricsData,
  useTileProps,
  useCollection,
  useContents,
} from "hooks";
import type { FunctionComponent } from "react";
import React, { useCallback, useContext, useEffect, useMemo, useState, startTransition } from "react";
import { ContentFilterContext } from "screens/content";
import { useAddToCharliContext } from "screens/panels/addToCharli/AddToCharliWizard/AddToCharliProvider";
import { getItemTypeForContent } from "screens/content/common/utils";
import type { SupportedItemKeys } from "state/selection/reducer";
import { AnswerStatus } from "types/question";
import { QuestionAnswerResults } from "./QuestionAnswerResults";
import { HighlightsSimpleList } from "screens/panels/highlights/HighlightsSimpleList";
import { GapAnalysisResults } from "./GapAnalysisResults";
import { AssessmentResultsQuestion } from "./AssessmentResultsQuestion";
import orderBy from "lodash/orderBy";
import { ContentViewExtractedAISection } from "screens/content/contentView/ContentViewExtractedAISection";
import { SectionContentLayout } from "../components/utils";
import { StockEquityChart } from "../components/StockEquityChart";
import { BlockSectionHeader } from "screens/content/contentView/previewSection/BlockSectionHeader";
import { ProjectDetailViewSkeleton } from "../ProjectDetailViewSkeleton";
import { formatDateWithOutputFormat } from "screens/common/modal/formatters";
import type { AssessmentResultsData } from "types/collection";
import DataAnalysisGif from "screens/common/static/images/data-analysis.gif";
import { useWorkflowKey } from "hooks/useWorkflows";
import { useProjectQuestionsContext } from "../ProjectQuestionsContext";
import QuadrantChart from "screens/landing/tabs/collections/chartComponents/QuadrantChart/QuadrantChart";
import { TiThumbsUp, TiThumbsDown } from "react-icons/ti";
import { SmallActionButton } from "screens/content/contentCanvas/cell/SmallActionButton";
import { generateQuadrantData } from "screens/landing/tabs/collections/chartComponents/data/quadrantData";
import type { StockEquityData } from "types/stock";

interface Props {
  collectionId: string;
  isLoading: boolean;
}

export const ProjectDetailDefaultAIResults: FunctionComponent<React.PropsWithChildren<React.PropsWithChildren<Props>>> = ({
  collectionId,
  isLoading,
}) => {
  const { setSelectedItems, resetSelectedItems } = useItemSelector();
  const { searchText, setResultsDataCount, availableContent } = useContext(ContentFilterContext);
  const { isHighlightsPanelOpen, isAddToCollectionModalOpen } = useAddToCharliContext();
  const configMap = useConfigMap();
  const collectionMetadataIds = useCollectionKey(collectionId, "metadataIds");
  const collectionType = useCollectionKey(collectionId, "collectionType");
  const collectionExtractedHighlights = useCollectionKey(collectionId, "extractedHighlights");
  const { questions: collectionQuestions } = useProjectQuestionsContext();
  const collectionUnstructuredData = useCollectionKey(collectionId, "unstructuredData");
  const isMobile = useBreakpointValue({ base: true, md: false }, { fallback: "md", ssr: false });
  const aiResultResourceSections = useGetViewConfig("aiResultResourceSections", collectionType, configMap);
  // TODO: change this to use tags
  const companyStockContentData = useContents(collectionMetadataIds, {
    refreshFromNetwork: true,
    filterByName: "stock equity data",
  });

  const companyStockData = useMemo((): StockEquityData | null => {
    if (companyStockContentData && companyStockContentData.length > 0) {
      const dynamicData = companyStockContentData[0].dynamicData?.dynamic_data;

      if (!dynamicData) return null;

      if (dynamicData && typeof dynamicData === "object" && "data" in dynamicData) {
        return dynamicData as StockEquityData;
      }

      return null;
    }
    return null;
  }, [companyStockContentData]);

  const companyLongName = companyStockData?.data.chart.result[0].meta.longName || "";

  const projectCreationDate = useCollectionCreatedDate(collectionId);
  const commonTileProps = useTileProps();
  const latestWorkflowId = useLatestCollectionWorkflowId(collectionId);
  const workflowStatus = useWorkflowKey(latestWorkflowId, "status");
  const textColor = useColorModeValue("primary.darkGray", "gray.400");
  const bgColor = useColorModeValue("white", "#161B25");
  const borderColor = useColorModeValue("gray.200", "gray.600");
  const { quadrantData } = useDashboardMetricsData("pi_quadrant_index", collectionId);
  const collection = useCollection(collectionId);
  const legacyQuadrantData = useMemo(() => generateQuadrantData(collection ? [collection] : undefined), [collection]);

  const quadrantDataToUse = useMemo(() => {
    const mergedDataMap = new Map();

    // Add legacy data first (lower priority)
    if (legacyQuadrantData && legacyQuadrantData.length > 0) {
      legacyQuadrantData.forEach((item) => {
        if (item.projectId) {
          mergedDataMap.set(item.projectId, item);
        }
      });
    }

    // Add quadrant data (higher priority - will overwrite legacy data for same projectId)
    if (quadrantData && quadrantData.length > 0) {
      quadrantData.forEach((item) => {
        if (item.projectId) {
          mergedDataMap.set(item.projectId, item);
        }
      });
    }

    return Array.from(mergedDataMap.values());
  }, [quadrantData, legacyQuadrantData]);

  const assessmentResultsData = useMemo(() => {
    if (!collectionUnstructuredData?.data.assessment_results) {
      return [];
    }
    return collectionUnstructuredData?.data.assessment_results;
  }, [collectionUnstructuredData]);

  const [filteredQuestions, setFilteredQuestions] = useState<AssessmentResultsData[]>(assessmentResultsData);

  useEffect(() => {
    if (availableContent?.length) {
      const items =
        availableContent &&
        availableContent.reduce((cells, cell) => {
          const itemType = getItemTypeForContent(cell.type);
          cells[cell.mediaId] = { type: itemType };
          return cells;
        }, {} as { [id: string]: { type: SupportedItemKeys } });
      if (items) setSelectedItems(items);
    } else {
      resetSelectedItems();
    }
  }, [availableContent?.length, isHighlightsPanelOpen, isAddToCollectionModalOpen, availableContent, setSelectedItems, resetSelectedItems]);

  const extractedHighlightsData = useMemo(() => {
    if (!collectionExtractedHighlights) {
      return [];
    }
    return collectionExtractedHighlights;
  }, [collectionExtractedHighlights]);

  const allQuestionAnswers = useMemo(() => {
    return (collectionQuestions ?? []).filter((question) => ![AnswerStatus.unpinned, AnswerStatus.removed].includes(question.status));
  }, [collectionQuestions]);

  const filteredQuestionAnswers = useMemo(() => {
    if (!searchText) return allQuestionAnswers;
    return allQuestionAnswers.filter(
      (question) =>
        question.question.toLowerCase().includes(searchText.toLowerCase()) ||
        question.answers.some((answer) => answer.answer.toLowerCase().includes(searchText.toLowerCase()))
    );
  }, [allQuestionAnswers, searchText]);

  const loadMoreQuestions = useCallback(
    async (token: string | null) => {
      const PAGE_SIZE = 5;
      const offset = token ? parseInt(token) : 0;

      // Simulate network delay with a random duration to make loading feel more natural
      await new Promise((resolve) => setTimeout(resolve, Math.random() * 300 + 200));

      const nextBatch = filteredQuestionAnswers.slice(offset, offset + PAGE_SIZE);
      const hasMore = offset + PAGE_SIZE < filteredQuestionAnswers.length;
      const nextToken = hasMore ? (offset + PAGE_SIZE).toString() : null;

      return {
        data: nextBatch,
        nextToken,
        totalCount: filteredQuestionAnswers.length,
      };
    },
    [filteredQuestionAnswers]
  );

  const gapAnalysisData = useMemo(() => {
    if (!collectionUnstructuredData?.data.gap_analysis_results) {
      return [];
    }
    return collectionUnstructuredData?.data.gap_analysis_results;
  }, [collectionUnstructuredData]);

  const aiResultResourceSectionContent = useMemo(() => {
    if (!aiResultResourceSections) {
      return [];
    }

    const sectionNamesToInclude = aiResultResourceSections.flatMap((section) => {
      if (typeof section === "string" && section.includes(",")) {
        return section.split(",").map((s) => s.trim());
      }
      return section;
    });

    return orderBy(
      availableContent?.filter((content) =>
        content.detailSections.some(
          (detailSection) => sectionNamesToInclude.includes(detailSection.sectionName) && detailSection.data.length > 0
        )
      ),
      "name"
    );
  }, [aiResultResourceSections, availableContent]);

  const orderByRiskLevel = useCallback(() => {
    const riskLevelEnum = {
      "Low Risk": 1,
      "Medium Risk": 2,
      "High Risk": 3,
    };

    return orderBy(filteredQuestions, [(result) => riskLevelEnum[result.risk_assessment_result.risk_level.replace(".", "")]], ["desc"]);
  }, [filteredQuestions]);

  const hasGeneratedContentQA = useMemo(() => {
    return allQuestionAnswers.length > 0;
  }, [allQuestionAnswers.length]);

  const hasGeneratedContentGap = useMemo(() => {
    return gapAnalysisData.length > 0;
  }, [gapAnalysisData.length]);

  const hasGeneratedContentRisk = useMemo(() => {
    return orderByRiskLevel().length > 0;
  }, [orderByRiskLevel]);

  const hasExtractedContentSourceSections = useMemo(() => {
    return aiResultResourceSectionContent && aiResultResourceSectionContent.length > 0;
  }, [aiResultResourceSectionContent]);

  const hasExtractedContentHighlights = useMemo(() => {
    return extractedHighlightsData.length > 0;
  }, [extractedHighlightsData.length]);

  const hasGeneratedContentSectionCount =
    Number(hasGeneratedContentQA) + Number(hasGeneratedContentGap) + Number(hasGeneratedContentRisk) || 0;
  const hasExtractedContentSectionCount = Number(hasExtractedContentSourceSections) + Number(hasExtractedContentHighlights) || 0;

  useEffect(() => {
    setResultsDataCount(Math.max(0, hasGeneratedContentSectionCount + hasExtractedContentSectionCount));
  }, [hasExtractedContentSectionCount, hasGeneratedContentSectionCount, setResultsDataCount]);

  useEffect(() => {
    if (searchText && searchText.length > 0) {
      startTransition(() => {
        setFilteredQuestions(
          assessmentResultsData.filter(
            (result) =>
              result.section.question.includes(searchText) ||
              result.section.answer.includes(searchText) ||
              result.followup_questions.some((question) => question.question.includes(searchText))
          )
        );
      });
    } else {
      setFilteredQuestions(assessmentResultsData);
    }
  }, [assessmentResultsData, searchText]);

  const DataAnalysisMessage = () => (
    <Center>
      <Stack direction="row" spacing="1rem" justifyContent={"space-between"} {...commonTileProps} borderRadius={"none"}>
        <Center>
          <Image src={DataAnalysisGif} alt="AI Results Empty" width={isMobile ? "4rem" : "6rem"} />
        </Center>
        <Stack width="100%" color={textColor}>
          <Text textAlign={"left"} fontSize="sm">
            {`I am collecting the data and running a full analysis${
              companyLongName ? ` on ${companyLongName}` : ""
            }. I will send you an email with the summary report shortly.`}
          </Text>
          <Text textAlign={"left"} fontSize="sm">
            {`Once complete you can ask detailed questions and dive deeper. You can kick-off multiple projects at the same time and I will work on all of them in parallel.`}
          </Text>
        </Stack>
      </Stack>
    </Center>
  );

  return (
    <ProjectDetailViewSkeleton isLoading={isLoading} height="100%">
      <SectionContentLayout useGrid className="ch-ai-result-sections">
        {(quadrantDataToUse.length > 0 || hasExtractedContentSourceSections || hasExtractedContentHighlights) && (
          <SectionContentLayout useGrid={false} className="ch-sections-generated">
            {quadrantDataToUse && (
              <Box width="100%" bg={bgColor} border="1px solid" borderColor={borderColor} borderRadius="md">
                <Box pt={isMobile ? ".5rem" : "1rem"} pb={isMobile ? "0" : ".5rem"}>
                  <BlockSectionHeader style={{ paddingLeft: isMobile ? ".5rem" : "1rem" }} title={`Pi Quadrant Index`} />
                </Box>
                <Box height={isMobile ? "15rem" : "19rem"} pl="1rem" pr={isMobile ? ".5rem" : "1rem"}>
                  <QuadrantChart data={quadrantDataToUse} collectionId={collectionId} />
                </Box>
                <Stack
                  height="2rem"
                  spacing={isMobile ? ".5rem" : "1rem"}
                  width="100%"
                  px={isMobile ? ".5rem" : "1rem"}
                  py={isMobile ? ".2rem" : ".5rem"}
                  direction="row"
                  borderTop="1px solid"
                  borderColor={borderColor}
                  align="center">
                  <SmallActionButton classNames="ch-like-section" iconTypeName={TiThumbsUp} tooltip={"Like Section"} />
                  <SmallActionButton classNames="ch-unlike-section" iconTypeName={TiThumbsDown} tooltip={"Unlike Section"} />
                </Stack>
              </Box>
            )}

            {companyStockData && (
              <Box width="100%" bg={bgColor} border="1px solid" borderColor={borderColor} borderRadius="md">
                <Box pt={isMobile ? ".5rem" : "1rem"} pb={isMobile ? "0" : ".5rem"}>
                  <BlockSectionHeader
                    style={{ paddingLeft: isMobile ? ".5rem" : "1rem" }}
                    title={`Stock Equity Chart ${
                      projectCreationDate && "as of " + formatDateWithOutputFormat(new Date(projectCreationDate), "do MMM yyyy hh:mm")
                    }`}
                  />
                </Box>
                <Box pr={isMobile ? ".5rem" : "1rem"}>
                  <StockEquityChart stockEquityData={companyStockData} />
                </Box>
                <Stack
                  height="2rem"
                  spacing={isMobile ? ".5rem" : "1rem"}
                  width="100%"
                  px={isMobile ? ".5rem" : "1rem"}
                  py={isMobile ? ".2rem" : ".5rem"}
                  direction="row"
                  borderTop="1px solid"
                  borderColor={borderColor}
                  align="center">
                  <SmallActionButton classNames="ch-like-section" iconTypeName={TiThumbsUp} tooltip={"Like Section"} />
                  <SmallActionButton classNames="ch-unlike-section" iconTypeName={TiThumbsDown} tooltip={"Unlike Section"} />
                </Stack>
              </Box>
            )}
            {aiResultResourceSectionContent &&
              aiResultResourceSectionContent.map((contentDetail, index) => (
                <SectionContentLayout useGrid={false} className={`section-${index}`} key={`section-${index}`}>
                  <ContentViewExtractedAISection
                    showEmptyValues={false}
                    contentDetails={contentDetail}
                    sectionTitle={contentDetail.name}
                    includedSections={aiResultResourceSections}
                    isEditModeEnabled={false}
                    valueColumnAlignRight
                    isTextTruncated
                    collectionId={collectionId}
                  />
                </SectionContentLayout>
              ))}
            {hasExtractedContentHighlights && (
              <HighlightsSimpleList extractedHighlights={extractedHighlightsData} searchPhrase={searchText} />
            )}
          </SectionContentLayout>
        )}
        <SectionContentLayout useGrid={false} className="layout-sections-extracted">
          {hasGeneratedContentQA ? (
            <QuestionAnswerResults
              collectionId={collectionId}
              questionAnswers={filteredQuestionAnswers}
              loadMoreQuestions={loadMoreQuestions}
            />
          ) : (
            collectionType === "due_diligence" && workflowStatus === "in_progress" && <DataAnalysisMessage />
          )}
          {hasGeneratedContentGap && <GapAnalysisResults gapAnalysisResults={gapAnalysisData} />}
          {hasGeneratedContentRisk && <AssessmentResultsQuestion assessmentResults={orderByRiskLevel()} searchText={searchText} />}
        </SectionContentLayout>
      </SectionContentLayout>
    </ProjectDetailViewSkeleton>
  );
};
